/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import theme from "../../../theme/settings";
import generateRandom from "../../../utils/randomString";
import Typography from "../../typography";
import { itemsCenter, cursorNoDrop, cursorPointer, displayBlock, flexRow, positionAbsolute, positionRelative } from "../../../styles/general";
const {
  colors: {
    system: {
      rest: clRest,
      active: clActive,
      disabled: systemDisabled,
      active: systemActive
    },
    text: {
      main: clTextMain
    },
    fill: {
      focus: clFillFocus,
      hover: fillHover
    },
    white: clWhite,
    semantic: {
      danger: clDanger
    }
  },
  spacing
} = theme;
const Toggle = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  id,
  disabled,
  readOnly,
  name,
  height,
  width,
  label,
  checked,
  defaultChecked,
  onChange,
  inputRef,
  inputProps,
  lineClamp,
  hoverTooltip,
  required,
  ...props
}, ref) => {
  if (!inputRef) {
    inputRef = useRef(null);
  }
  const [checkedState, setCheckedState] = useState(Boolean(checked !== undefined ? checked : defaultChecked));
  const padding = spacing(0.5);
  const formCheck = css`
                ${positionRelative};
                min-height: ${height}px;
                padding: ${padding}px;
            `;
  const toggle = css`
                ${flexRow};
                ${itemsCenter};
                ${positionRelative};
                ${cursorPointer};
                margin: 0;
            `;
  const slider = css`
                ${positionRelative};
                ${displayBlock};
                height: ${height}px;
                width: ${width}px;
                min-width: ${width}px;
                border-radius: ${height}px;
                background: ${clWhite};
                outline: 1px solid ${clRest};
                &.disabled {
                    outline: 1px solid ${systemDisabled} !important;
                }
                &.disabled,
                &.read-only {
                    ${cursorNoDrop};
                }
                &::after {
                    ${positionAbsolute};
                    background: ${clRest};
                    width: ${height - spacing(1)}px;
                    height: ${height - spacing(1)}px;
                    content: '';
                    top: 2px;
                    left: 2px;
                    border-radius: 100%;
                    transition: 0.3s ease-in-out;
                    margin: 0px auto;
                }
                &:not(.disabled):not(.read-only) {
                    &:active,
                    &:focus {
                        box-shadow: 0 0 0 2px ${clFillFocus};
                    }
                }
            `;
  const checkbox = css`
                left: 0;
                &:checked + label > span.css-${slider.name} {
                    background: ${systemActive};
                    outline-color: ${systemActive};
                    &::after {
                        background: ${clWhite};
                        left: ${width - height}px;
                    }
                    &.disabled {
                        background: ${systemDisabled} !important;
                    }
                }
                &:not(:checked) + label > span.css-${slider.name} {
                    &:not(.disabled):not(.read-only) {
                        &:hover {
                            box-shadow: 0 0 0 2px ${fillHover};
                            outline-color: ${systemActive};
                            background-color: ${fillHover};
                        }
                        &:hover:after {
                            background-color: ${clActive};
                        }
                        &:active,
                        &:focus {
                            background-color: ${clFillFocus};
                        }
                    }
                    &.disabled:after {
                        background: ${systemDisabled} !important;
                    }
                }
            `;
  const idDefault = generateRandom(10, {
    allowNumber: false,
    allowSymbol: false
  });
  const handleChange = () => {
    if (disabled || readOnly) return;
    setCheckedState(checked !== undefined ? Boolean(checked) : !checkedState);
    const e = {
      value: !checkedState,
      target: {
        ...inputRef.current,
        checked: !checkedState
      }
    };
    if (onChange) onChange(e);
  };
  useEffect(() => {
    if (checked !== undefined) setCheckedState(Boolean(checked));
  }, [checked]);
  return useMemo(() => jsx("div", {
    id: 'DGN-Core-Toggle-' + (id ? id : idDefault),
    css: formCheck,
    ref: ref,
    ...props
  }, jsx("input", {
    ...inputProps,
    ref: inputRef,
    hidden: true,
    name: name,
    type: "checkbox",
    disabled: disabled,
    readOnly: readOnly,
    css: checkbox,
    checked: checkedState,
    onChange: handleChange
  }), jsx("label", {
    css: toggle,
    onClick: handleChange
  }, jsx("span", {
    css: slider,
    className: `${disabled ? 'disabled' : ''} ${readOnly ? 'read-only' : ''}`
  }), label !== undefined && jsx(Typography, {
    type: 'p1',
    style: {
      color: disabled ? systemDisabled : clTextMain,
      marginBottom: 0,
      marginLeft: spacing(2.5)
    },
    hoverTooltip: hoverTooltip,
    lineClamp: lineClamp || null,
    mapping: 'label'
  }, jsx(Fragment, null, label, required && jsx("span", {
    style: {
      color: disabled ? systemDisabled : clDanger
    }
  }, "*"))))), [disabled, onChange, checkedState, readOnly]);
}));
Toggle.defaultProps = {
  disabled: false,
  readOnly: false,
  defaultChecked: false,
  label: '',
  value: '',
  id: '',
  width: 40,
  height: 20,
  lineClamp: null,
  required: false
};
Toggle.propTypes = {
  /** * If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** prevent all event if true */
  readOnly: PropTypes.bool,
  /** If `true`, the component is defaultChecked. */
  defaultChecked: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([0, 1])]),
  /** If `true`, the component is checked. */
  checked: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([0, 1])]),
  /** Id is randomized randomly to avoid duplication. */
  id: PropTypes.string,
  /** Name attribute of the `input` element. */
  name: PropTypes.string,
  /** The name of the Radio is displayed on the interface */
  label: PropTypes.string,
  /** height is a number, height of Toggle default 20 */
  height: PropTypes.number,
  /** width is a number, width of Toggle default 40 */
  width: PropTypes.number,
  /** props for input */
  inputProps: PropTypes.object,
  /** The line-clamp property truncates text at a specific number of Label. */
  lineClamp: PropTypes.number,
  /** Shows tooltip containing Label on hover */
  hoverTooltip: PropTypes.bool,
  /** * Callback fired when the state is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onChange: PropTypes.func,
  /** If `true`, the label will indicate that the checkbox is required. */
  required: PropTypes.bool
};
export default Toggle;